import React, { useState, useEffect } from "react";
import axios from "axios";
import Movie from "./Movie";
import { useNavigate } from "react-router-dom";
import LiquidCard from "./LiquidCard";
import { useLanguage } from "../contexts/LanguageContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../config/firebasedeconfig";
import Flag from "react-world-flags";
import { findQuebecRating } from "../utils/quebecRatingUtils";

// Liste des pays prioritaires avec leurs codes ISO et drapeaux
const priorityCountries = [
  { code: "FR", name: "France", flag: "🇫🇷" },
  { code: "CA", name: "Canada", flag: "🇨🇦" },
  { code: "US", name: "États-Unis", flag: "🇺🇸" },
  { code: "GB", name: "Royaume-Uni", flag: "🇬🇧" },
  { code: "DE", name: "Allemagne", flag: "🇩🇪" },
  { code: "ES", name: "Espagne", flag: "🇪🇸" },
  { code: "IT", name: "Italie", flag: "🇮🇹" },
  { code: "JP", name: "Japon", flag: "🇯🇵" },
  { code: "KR", name: "Corée du Sud", flag: "🇰🇷" },
  { code: "IN", name: "Inde", flag: "🇮🇳" },
  { code: "BR", name: "Brésil", flag: "🇧🇷" },
  { code: "MX", name: "Mexique", flag: "🇲🇽" },
  { code: "AU", name: "Australie", flag: "🇦🇺" },
  { code: "RU", name: "Russie", flag: "🇷🇺" },
  { code: "CN", name: "Chine", flag: "🇨🇳" },
];

const QUEBEC_FILTER_CODE = "QC";

const SEARCH_RESULTS_PER_PAGE = 20;
const QUEBEC_TMDB_PAGES_PER_BATCH = 5;

/*
 * Mémorise les résultats déjà vérifiés afin de ne pas
 * recommencer à la première page TMDB à chaque navigation.
 */
const quebecSearchCache = new Map();

const QUEBEC_NATION_CODES = new Set([
  "QUE",
  "QUÉBEC",
  "QUEBEC",
]);

const hasQuebecNation = (ratingRecord) =>
  Array.isArray(ratingRecord?.nations) &&
  ratingRecord.nations.some((nation) =>
    QUEBEC_NATION_CODES.has(
      String(nation).trim().toUpperCase()
    )
  );

const filterQuebecProductions = async (movies) => {
  const checkedMovies = await Promise.all(
    movies.map(async (movie) => {
      const quebecRecord = await findQuebecRating({
        tmdbId: movie.id,
        title: movie.title,
        originalTitle: movie.original_title,
        releaseDate: movie.release_date,
      });

      if (!hasQuebecNation(quebecRecord)) {
        return null;
      }

      return {
        ...movie,
        isQuebecProduction: true,
        quebecRatingRecord: quebecRecord,
      };
    })
  );

  return checkedMovies.filter(Boolean);
};

const fetchQuebecResultsPage = async ({
  baseUrl,
  movieFrenzyPage,
}) => {
  let cacheEntry = quebecSearchCache.get(baseUrl);

  if (!cacheEntry) {
    cacheEntry = {
      movies: [],
      seenMovieIds: new Set(),
      nextTmdbPage: 1,
      totalTmdbPages: 500,
    };

    quebecSearchCache.set(baseUrl, cacheEntry);
  }

  const requestedResultCount =
    movieFrenzyPage * SEARCH_RESULTS_PER_PAGE;

  while (
    cacheEntry.movies.length < requestedResultCount &&
    cacheEntry.nextTmdbPage <= cacheEntry.totalTmdbPages
  ) {
    const lastTmdbPage = Math.min(
      cacheEntry.nextTmdbPage +
      QUEBEC_TMDB_PAGES_PER_BATCH -
      1,
      cacheEntry.totalTmdbPages
    );

    const tmdbPages = Array.from(
      {
        length:
          lastTmdbPage -
          cacheEntry.nextTmdbPage +
          1,
      },
      (_, index) =>
        cacheEntry.nextTmdbPage + index
    );

    const pageResponses = await Promise.all(
      tmdbPages.map((tmdbPage) =>
        axios.get(
          `${baseUrl}&page=${tmdbPage}`
        )
      )
    );

    pageResponses.forEach((response) => {
      const reportedTotalPages = Number(
        response.data.total_pages
      );

      if (Number.isFinite(reportedTotalPages)) {
        cacheEntry.totalTmdbPages = Math.min(
          reportedTotalPages,
          500
        );
      }
    });

    const newCandidates = pageResponses
      .flatMap(
        (response) =>
          response.data.results || []
      )
      .filter((movie) => {
        if (
          cacheEntry.seenMovieIds.has(movie.id)
        ) {
          return false;
        }

        cacheEntry.seenMovieIds.add(movie.id);
        return true;
      });

    const newQuebecMovies =
      await filterQuebecProductions(
        newCandidates
      );

    cacheEntry.movies.push(
      ...newQuebecMovies
    );

    cacheEntry.nextTmdbPage =
      lastTmdbPage + 1;
  }

  const firstResultIndex =
    (movieFrenzyPage - 1) *
    SEARCH_RESULTS_PER_PAGE;

  return cacheEntry.movies.slice(
    firstResultIndex,
    firstResultIndex +
    SEARCH_RESULTS_PER_PAGE
  );
};

const SearchMovie = () => {
  const { t, language } = useLanguage();
  // Le hook vient tout de suite après la déclaration du tableau
  const [searchType, setSearchType] = useState({ key: "movie" }); // Initialiser avec juste la clé
  const [data, setData] = useState([]);
  const [query, setQuery] = useState("");
  const history = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [submittedAdvancedFilters, setSubmittedAdvancedFilters] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearchError, setHasSearchError] = useState(false);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] =
    useState(false);

  // États pour la recherche avancée
  const [advancedFilters, setAdvancedFilters] = useState({
    actorName: "",
    selectedCountries: [],
    yearFrom: new Date().getFullYear() - 10,
    yearTo: new Date().getFullYear(),
    genres: [],
    minRating: 0,
    ratingSource: "tmdb"
  });

  // Fonction pour traduire les pays
  const getCountryName = (countryCode) => {
    const countryMap = {
      'FR': t('search.countries.france'),
      'CA': t('search.countries.canada'),
      'US': t('search.countries.unitedStates'),
      'GB': t('search.countries.unitedKingdom'),
      'DE': t('search.countries.germany'),
      'ES': t('search.countries.spain'),
      'IT': t('search.countries.italy'),
      'JP': t('search.countries.japan'),
      'KR': t('search.countries.southKorea'),
      'IN': t('search.countries.india'),
      'BR': t('search.countries.brazil'),
      'MX': t('search.countries.mexico'),
      'AU': t('search.countries.australia'),
      'RU': t('search.countries.russia'),
      'CN': t('search.countries.china')
    };
    return countryMap[countryCode] || countryCode;
  };

  // Tableau des pays avec traduction dynamique
  const getTranslatedCountries = () => {
    return priorityCountries.map(country => ({
      ...country,
      name: getCountryName(country.code)
    }));
  };

  const insertQuebecAfterCanada = (countries) =>
    countries.flatMap((country) =>
      country.code === "CA"
        ? [
          country,
          {
            code: QUEBEC_FILTER_CODE,
            name: "Québec",
            isQuebec: true,
          },
        ]
        : [country]
    );

  const getTranslatedCountryOptions = () =>
    insertQuebecAfterCanada(
      getTranslatedCountries()
    );

  const getTranslatedAdvancedCountries = () =>
    insertQuebecAfterCanada(
      getTranslatedCountries().slice(0, 8)
    );

  const renderCountryFlag = (country) =>
    country?.isQuebec ? (
      <img
        src={`${process.env.PUBLIC_URL}/images/quebecFlag.svg`}
        className="country-filter-flag"
        alt=""
        aria-hidden="true"
      />
    ) : (
      <Flag
        code={country?.code}
        className="country-filter-flag"
        alt=""
        aria-hidden="true"
      />
    );

  // Tableau des types de recherche avec traduction dynamique
  const getTranslatedSearchTypes = () => {
    return [
      {
        key: "movie",
        icon: "🎬",
        title: t('search.searchTypes.movie.title'),
        text: t('search.searchTypes.movie.text'),
        placeholder: t('search.searchTypes.movie.placeholder'),
      },
      {
        key: "actor",
        icon: "🧑‍🎤",
        title: t('search.searchTypes.actor.title'),
        text: t('search.searchTypes.actor.text'),
        placeholder: t('search.searchTypes.actor.placeholder'),
      },
      {
        key: "country",
        icon: "🌍",
        title: t('search.searchTypes.country.title'),
        text: t('search.searchTypes.country.text'),
        placeholder: t('search.searchTypes.country.placeholder'),
      },
      {
        key: "advanced",
        icon: "⚙️",
        title: t('search.searchTypes.advanced.title'),
        text: t('search.searchTypes.advanced.text'),
        placeholder: t('search.searchTypes.advanced.placeholder'),
      },
    ];
  };

  const getPersonRole = (person) =>
    person?.known_for_department?.toLowerCase() === "directing"
      ? "director"
      : "actor";

  const openPersonDetails = (person) => {
    const role = getPersonRole(person);

    history(`/acteurDetails/${person.id}?role=${role}`);
  };

  const getPersonDepartmentLabel = (department) => {
    const normalizedDepartment = department?.toLowerCase();

    if (normalizedDepartment === "acting") {
      return t("search.actor.departments.acting");
    }

    if (normalizedDepartment === "directing") {
      return t("search.actor.departments.directing");
    }

    return department;
  };

  // Fonction pour obtenir le drapeau du pays sélectionné
  const getCountryFlag = (countryCode) => {
    const country = priorityCountries.find(c => c.code === countryCode);
    return country ? country.flag : "";
  };

  // Genres de films disponibles - exactement comme dans Navigation.js
  const movieGenres = [
    { id: 28, name: t('categories.action') },
    { id: 16, name: t('categories.animation') },
    { id: 12, name: t('categories.adventure') },
    { id: 35, name: t('categories.comedy') },
    { id: 80, name: t('categories.crime') },
    { id: 99, name: t('categories.documentary') },
    { id: 18, name: t('categories.drama') },
    { id: 10751, name: t('categories.family') },
    { id: 14, name: t('categories.fantasy') },
    { id: 10752, name: t('categories.war') },
    { id: 36, name: t('categories.history') },
    { id: 27, name: t('categories.horror') },
    { id: 10402, name: t('categories.music') },
    { id: 9648, name: t('categories.mystery') },
    { id: 10749, name: t('categories.romance') },
    { id: 878, name: t('categories.sciFi') },
    { id: 53, name: t('categories.thriller') },
    { id: 10770, name: t('categories.tvMovie') },
    { id: 37, name: t('categories.western') }
  ];

  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    setQuery("");
    setData([]);
    setCurrentPage(1);
    setSubmittedAdvancedFilters(null);
    setIsLoading(false);
    setHasSearchError(false);
    setIsCountryDropdownOpen(false);
  };

  // Initialiser searchType au démarrage
  useEffect(() => {
    const translatedTypes = getTranslatedSearchTypes();
    const initialType = translatedTypes.find(type => type.key === "movie");
    if (initialType) {
      setSearchType(initialType);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mettre à jour searchType quand la langue change
  useEffect(() => {
    const translatedTypes = getTranslatedSearchTypes();
    const currentTypeKey = searchType.key;
    const updatedType = translatedTypes.find(type => type.key === currentTypeKey);
    if (updatedType) {
      setSearchType(updatedType);
    }
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let isCancelled = false;

    if (!query && searchType.key !== "advanced") {
      setData([]);
      setIsLoading(false);
      setHasSearchError(false);
      return;
    }

    if (searchType.key === "advanced") {
      if (!submittedAdvancedFilters) {
        setData([]);
        setIsLoading(false);
        setHasSearchError(false);
        return;
      }

      const defaultYearFrom = new Date().getFullYear() - 10;
      const defaultYearTo = new Date().getFullYear();

      const hasYearFilter =
        submittedAdvancedFilters.yearFrom !== defaultYearFrom ||
        submittedAdvancedFilters.yearTo !== defaultYearTo;

      const hasActiveFilters =
        submittedAdvancedFilters.actorName ||
        submittedAdvancedFilters.selectedCountries.length > 0 ||
        submittedAdvancedFilters.genres.length > 0 ||
        submittedAdvancedFilters.minRating > 0 ||
        hasYearFilter;

      if (!hasActiveFilters) {
        setData([]);
        setIsLoading(false);
        setHasSearchError(false);
        return;
      }
    }

    const fetchMovies = async () => {
      setIsLoading(true);
      setHasSearchError(false);
      setData([]);

      try {
        let URL = "";
        let shouldCollectQuebecResults = false;

        const API_KEY = "e16900047627f56531a2d3d8d4df9e92";

        const getApiLanguage = () => {
          const langMap = {
            fr: "fr-FR",
            en: "en-US",
            es: "es-ES",
            de: "de-DE"
          };

          return langMap[language] || "fr-FR";
        };

        const apiLang = getApiLanguage();

        if (
          searchType.key === "country" &&
          query === QUEBEC_FILTER_CODE
        ) {
          const quebecSearchUrl =
            `https://api.themoviedb.org/3/discover/movie` +
            `?api_key=${API_KEY}` +
            `&language=${apiLang}` +
            `&with_origin_country=CA` +
            `&sort_by=popularity.desc`;

          const quebecMovies =
            await fetchQuebecResultsPage({
              baseUrl: quebecSearchUrl,
              movieFrenzyPage: currentPage,
            });

          if (!isCancelled) {
            setData(quebecMovies);
          }

          return;
        }

        if (searchType.key === "movie") {
          URL = `https://api.themoviedb.org/3/search/movie?api_key=${API_KEY}&language=${apiLang}&query=${encodeURIComponent(query.trim())}&page=${currentPage}`;
        } else if (searchType.key === "actor") {
          URL = `https://api.themoviedb.org/3/search/person?api_key=${API_KEY}&language=${apiLang}&query=${encodeURIComponent(query.trim())}&page=${currentPage}`;
        } else if (searchType.key === "country") {
          URL =
            `https://api.themoviedb.org/3/discover/movie` +
            `?api_key=${API_KEY}` +
            `&language=${apiLang}` +
            `&with_origin_country=${encodeURIComponent(query)}` +
            `&sort_by=popularity.desc` +
            `&page=${currentPage}`;
        } else if (searchType.key === "advanced") {
          const filters = submittedAdvancedFilters;

          /*
           * La page TMDB sera ajoutée plus bas.
           * Le collecteur québécois gère lui-même sa pagination.
           */
          let advancedURL =
            `https://api.themoviedb.org/3/discover/movie` +
            `?api_key=${API_KEY}` +
            `&language=${apiLang}` +
            `&sort_by=popularity.desc`;

          const hasQuebecFilter =
            filters.selectedCountries.includes(
              QUEBEC_FILTER_CODE
            );

          const tmdbCountryCodes =
            filters.selectedCountries.filter(
              (countryCode) =>
                countryCode !== QUEBEC_FILTER_CODE
            );

          /*
           * Le filtrage québécois strict est utilisé lorsque
           * Québec est le seul pays sélectionné.
           */
          const hasOnlyQuebecCountryFilter =
            hasQuebecFilter &&
            tmdbCountryCodes.length === 0;

          /*
           * TMDB ne possède pas de code Québec.
           * Canada sert uniquement à obtenir les candidats.
           */
          if (
            hasQuebecFilter &&
            !tmdbCountryCodes.includes("CA")
          ) {
            tmdbCountryCodes.push("CA");
          }

          if (tmdbCountryCodes.length > 0) {
            advancedURL +=
              `&with_origin_country=${tmdbCountryCodes.join("|")}`;
          }

          advancedURL +=
            `&primary_release_date.gte=${filters.yearFrom}-01-01`;

          advancedURL +=
            `&primary_release_date.lte=${filters.yearTo}-12-31`;

          if (filters.genres.length > 0) {
            advancedURL +=
              `&with_genres=${filters.genres.join(",")}`;
          }

          if (
            filters.minRating > 0 &&
            filters.ratingSource === "tmdb"
          ) {
            advancedURL +=
              `&vote_average.gte=${filters.minRating}`;
          }

          URL = advancedURL;

          if (filters.actorName) {
            const actorRes = await axios.get(
              `https://api.themoviedb.org/3/search/person` +
              `?api_key=${API_KEY}` +
              `&language=${apiLang}` +
              `&query=${encodeURIComponent(
                filters.actorName.trim()
              )}`
            );

            if (actorRes.data.results.length > 0) {
              const actorId =
                actorRes.data.results[0].id;

              URL += `&with_cast=${actorId}`;
            } else {
              if (!isCancelled) {
                setData([]);
              }

              return;
            }
          }

          if (hasOnlyQuebecCountryFilter) {
            shouldCollectQuebecResults = true;
          } else {
            URL += `&page=${currentPage}`;
          }
        }

        if (URL) {
          let results;

          if (shouldCollectQuebecResults) {
            results = await fetchQuebecResultsPage({
              baseUrl: URL,
              movieFrenzyPage: currentPage,
            });
          } else {
            const res = await axios.get(URL);
            results = res.data.results || [];
          }

          const shouldFilterWithMovieFrenzyRating =
            searchType.key === "advanced" &&
            submittedAdvancedFilters?.ratingSource === "moviefrenzy" &&
            submittedAdvancedFilters?.minRating > 0;

          if (shouldFilterWithMovieFrenzyRating) {
            const minMovieFrenzyRating = Number(submittedAdvancedFilters.minRating);

            const resultsWithMovieFrenzyRatings = await Promise.all(
              results.map(async (movie) => {
                const ratingRef = doc(db, "movieRatings", String(movie.id));
                const ratingSnap = await getDoc(ratingRef);

                if (!ratingSnap.exists()) {
                  return null;
                }

                const ratingData = ratingSnap.data();
                const averageRating = Number(ratingData.averageRating);

                if (!Number.isFinite(averageRating)) {
                  return null;
                }

                if (averageRating < minMovieFrenzyRating) {
                  return null;
                }

                return {
                  ...movie,
                  movieFrenzyAverageRating: averageRating,
                  movieFrenzyRatingsCount: Number(ratingData.ratingsCount || 0),
                };
              })
            );

            results = resultsWithMovieFrenzyRatings.filter(Boolean);
          }

          if (!isCancelled) {
            setData(results);
          }
        }
      } catch (error) {
        console.error("Erreur lors de la recherche TMDB:", error);

        if (!isCancelled) {
          setData([]);
          setHasSearchError(true);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchMovies();

    return () => {
      isCancelled = true;
    };
  }, [query, currentPage, searchType, submittedAdvancedFilters, language]);


  // Fonction qui remplace l'image manquante avec une image de remplacement
  const replaceNoPoster = (event) => {
    event.target.onerror = null; // Pour éviter une boucle infinie si la substitution ne fonctionne pas
    event.target.src = "https://zupimages.net/up/23/13/yd61.jpg"; // Utiliser la même image de remplacement que dans Movie.js
    event.target.alt = "Aucune image pour ce film";
  };

  const nextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const prevPage = () => {
    setCurrentPage((prevPage) => (prevPage > 1 ? prevPage - 1 : prevPage));
  };

  const hasSubmittedAdvancedSearch =
    searchType.key === "advanced" && submittedAdvancedFilters !== null;

  const shouldShowResults =
    searchType.key === "advanced" ? hasSubmittedAdvancedSearch : !!query;

  const countryOptions =
    getTranslatedCountryOptions();

  const selectedCountryOption =
    countryOptions.find(
      (country) => country.code === query
    );

  return (
    <div className="searchMovie">
      {/* Bloc de sélection des types */}
      <div className="containerLiquidCard">
        <div className="liquidCard-row">
          {getTranslatedSearchTypes().map((props, i) => (
            <LiquidCard
              key={props.title}
              {...props}
              index={i}
              active={searchType.key === props.key}
              onClick={() => handleSearchTypeChange(props)}
            />
          ))}
        </div>
      </div>

      {/* Indicateur + input de recherche */}
      <div className="input-container">
        <div className="searchType-indicator">
          <span className="searchType-indicator__icon">{searchType.icon || (searchType.key === "movie" ? "🎬" : searchType.key === "actor" ? "🧑‍🎤" : searchType.key === "country" ? "🌍" : "⚙️")}</span>
          <span className="searchType-indicator__label">{t(`search.searchTypes.${searchType.key}.title`)}</span>
        </div>

        {searchType.key === "country" ? (
          // Select pour les pays
          <div
            className={`country-dropdown ${isCountryDropdownOpen ? "is-open" : ""
              }`}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setIsCountryDropdownOpen(false);
              }
            }}
          >
            <button
              type="button"
              className="input country-dropdown__trigger"
              aria-haspopup="listbox"
              aria-expanded={isCountryDropdownOpen}
              onClick={() =>
                setIsCountryDropdownOpen(
                  (previousValue) => !previousValue
                )
              }
            >
              <span className="country-dropdown__value">
                {selectedCountryOption ? (
                  <>
                    {renderCountryFlag(
                      selectedCountryOption
                    )}

                    <span>
                      {selectedCountryOption.name}
                    </span>
                  </>
                ) : (
                  <span>
                    {t(
                      `search.searchTypes.${searchType.key}.placeholder`
                    )}
                  </span>
                )}
              </span>

              <span
                className={`country-dropdown__chevron ${isCountryDropdownOpen ? "is-open" : ""
                  }`}
                aria-hidden="true"
              >
                ▼
              </span>
            </button>

            {isCountryDropdownOpen && (
              <div
                className="country-dropdown__menu"
                role="listbox"
              >
                <button
                  type="button"
                  className={`country-dropdown__option country-dropdown__option--placeholder ${query === "" ? "is-selected" : ""
                    }`}
                  role="option"
                  aria-selected={query === ""}
                  onClick={() => {
                    setQuery("");
                    setCurrentPage(1);
                    setIsCountryDropdownOpen(false);
                  }}
                >
                  {t(
                    `search.searchTypes.${searchType.key}.placeholder`
                  )}
                </button>

                <div className="country-dropdown__group-label">
                  {t("search.countries.popularCountries")}
                </div>

                {countryOptions.map((country) => (
                  <button
                    type="button"
                    key={country.code}
                    className={`country-dropdown__option ${query === country.code
                      ? "is-selected"
                      : ""
                      }`}
                    role="option"
                    aria-selected={
                      query === country.code
                    }
                    onClick={() => {
                      setQuery(country.code);
                      setCurrentPage(1);
                      setIsCountryDropdownOpen(false);
                    }}
                  >
                    {renderCountryFlag(country)}

                    <span>{country.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : searchType.key === "advanced" ? (
          // Interface de recherche avancée
          <div className="advanced-search-container">
            <div className="advanced-filters">
              {/* Recherche par acteur */}
              <div className="filter-group">
                <label style={{ marginBottom: '1rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>{t('search.advancedSearch.actor')}</label>
                <input
                  type="text"
                  className="input-small"
                  placeholder={t('search.advancedSearch.actorPlaceholder')}
                  value={advancedFilters.actorName}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, actorName: e.target.value })}
                />
              </div>

              {/* Sélection des pays */}
              <div className="filter-group">
                <label>{t('search.advancedSearch.productionCountries')}</label>
                <div className="countries-checkboxes">
                  {getTranslatedAdvancedCountries().map((country) => (
                    <label key={country.code} className="checkbox-label">
                      <input
                        type="checkbox"
                        value={country.code}
                        checked={advancedFilters.selectedCountries.includes(
                          country.code
                        )}
                        onChange={(event) => {
                          if (event.target.checked) {
                            setAdvancedFilters({
                              ...advancedFilters,
                              selectedCountries: [
                                ...advancedFilters.selectedCountries,
                                country.code,
                              ],
                            });
                          } else {
                            setAdvancedFilters({
                              ...advancedFilters,
                              selectedCountries:
                                advancedFilters.selectedCountries.filter(
                                  (countryCode) =>
                                    countryCode !== country.code
                                ),
                            });
                          }
                        }}
                      />

                      <span>
                        {country.isQuebec ? (
                          <img
                            src={`${process.env.PUBLIC_URL}/images/quebecFlag.svg`}
                            className="country-filter-flag"
                            alt=""
                            aria-hidden="true"
                          />
                        ) : (
                          <Flag
                            code={country.code}
                            className="country-filter-flag"
                            alt=""
                            aria-hidden="true"
                          />
                        )}

                        {" "}
                        {country.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Période */}
              <div className="filter-group">
                <label style={{ marginBottom: '1rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>{t('search.advancedSearch.releasePeriod')}</label>
                <div className="year-range">
                  <input
                    type="number"
                    className="input-year"
                    min="1900"
                    max={new Date().getFullYear()}
                    value={advancedFilters.yearFrom}
                    onChange={(e) => setAdvancedFilters({ ...advancedFilters, yearFrom: parseInt(e.target.value) })}
                  />
                  <span className="year-separator" style={{ marginBottom: '1rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>{t('search.advancedSearch.to')}</span>
                  <input
                    type="number"
                    className="input-year"
                    min="1900"
                    max={new Date().getFullYear()}
                    value={advancedFilters.yearTo}
                    onChange={(e) => setAdvancedFilters({ ...advancedFilters, yearTo: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              {/* Genres */}
              <div className="filter-group">
                <label>{t('search.advancedSearch.genres')}</label>
                <p className="advanced-help-text">
                  {t('search.advancedSearch.genresHint')}
                </p>
                <div className="genres-grid">
                  {movieGenres.map(genre => (
                    <label key={genre.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        value={genre.id}
                        checked={advancedFilters.genres.includes(genre.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAdvancedFilters({
                              ...advancedFilters,
                              genres: [...advancedFilters.genres, genre.id]
                            });
                          } else {
                            setAdvancedFilters({
                              ...advancedFilters,
                              genres: advancedFilters.genres.filter(g => g !== genre.id)
                            });
                          }
                        }}
                      />
                      <span>{genre.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Note minimale */}
              <div className="filter-group">
                <label className="minimum-rating-label">
                  {t('search.advancedSearch.minimumRating')}: {advancedFilters.minRating}/10
                </label>

                <div
                  className="advanced-rating-source-toggle"
                  aria-label={t('search.advancedSearch.ratingSourceAria')}
                >
                  <button
                    type="button"
                    className={`advanced-rating-source-toggle__btn ${advancedFilters.ratingSource === "tmdb" ? "is-active" : ""}`}
                    onClick={() => setAdvancedFilters({ ...advancedFilters, ratingSource: "tmdb" })}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/tmdb.png`}
                      alt="TMDB"
                    />
                    <span>TMDB</span>
                  </button>

                  <button
                    type="button"
                    className={`advanced-rating-source-toggle__btn ${advancedFilters.ratingSource === "moviefrenzy" ? "is-active" : ""}`}
                    onClick={() => setAdvancedFilters({ ...advancedFilters, ratingSource: "moviefrenzy" })}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/newColorLogoMovieFrenzy_01.png`}
                      alt="MovieFrenzy"
                    />
                    <span>MovieFrenzy</span>
                  </button>
                </div>

                <p className="advanced-help-text">
                  {advancedFilters.ratingSource === "tmdb"
                    ? t('search.advancedSearch.tmdbRatingHint')
                    : t('search.advancedSearch.movieFrenzyRatingHint')}
                </p>

                <input
                  type="range"
                  className="rating-slider"
                  min="0"
                  max="9"
                  step="0.5"
                  value={advancedFilters.minRating}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, minRating: parseFloat(e.target.value) })}
                />
              </div>

              {/* Bouton pour lancer la recherche avancée */}
              <button
                type="button"
                className="launch-search-btn"
                onClick={() => {
                  setCurrentPage(1);
                  setIsLoading(false);
                  setHasSearchError(false);
                  setSubmittedAdvancedFilters({
                    ...advancedFilters,
                    actorName: advancedFilters.actorName.trim()
                  });
                }}
              >
                <i className="fas fa-search"></i> {t('search.advancedSearch.launchSearch')}
              </button>

              {/* Bouton pour réinitialiser les filtres */}
              <button
                type="button"
                className="reset-filters-btn"
                onClick={() => {
                  setAdvancedFilters({
                    actorName: "",
                    selectedCountries: [],
                    yearFrom: new Date().getFullYear() - 10,
                    yearTo: new Date().getFullYear(),
                    genres: [],
                    minRating: 0,
                    ratingSource: "tmdb"
                  });
                  setSubmittedAdvancedFilters(null);
                  setData([]);
                  setCurrentPage(1);
                  setIsLoading(false);
                  setHasSearchError(false);
                }}
              >
                <i className="fas fa-undo"></i> {t('search.advancedSearch.resetFilters')}
              </button>
            </div>
          </div>
        ) : (
          // Input normal pour les autres types
          <input
            className="input"
            value={query}
            placeholder={t(`search.searchTypes.${searchType.key}.placeholder`)}
            onChange={(e) => setQuery(e.target.value)}
          />
        )}

        {/* Flèches animées intégrées dans le container */}
        {shouldShowResults && data.length > 0 && (
          <div className="results-indicator-inline">
            <div className="arrow-container left">
              <div className="arrow-down">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
            <div className="results-text">
              {data.length} {data.length > 1 ? t('search.results.foundPlural') : t('search.results.found')}
            </div>
            <div className="arrow-container right">
              <div className="arrow-down">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
      </div>

      {isLoading && (
        <div className="search-status search-status--loading">
          <span className="search-status__spinner"></span>
          <span>{t('search.results.loading')}</span>
        </div>
      )}

      {hasSearchError && (
        <div className="search-status search-status--error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{t('search.results.error')}</span>
        </div>
      )}

      {/* Résultats ou placeholder */}
      {!isLoading && shouldShowResults ? (
        <ul className="searchMovie-list">
          {searchType.key === "movie" ? (
            // Affichage pour les films
            data.map((m) => (
              <Movie
                movie={m}
                key={m.id}
                onClickMovie={() => history(`/ficheFilm/${m.id}`)}
                onError={replaceNoPoster}
              />
            ))
          ) : searchType.key === "actor" ? (
            // Affichage pour les acteurs et les réalisateurs
            data.map((actor) => (
              <div
                key={actor.id}
                className="actor-search-card"
                onClick={() => openPersonDetails(actor)}
              >
                <div className="actor-image-container">
                  <img
                    className="actor-image"
                    src={
                      actor.profile_path
                        ? `https://image.tmdb.org/t/p/w200${actor.profile_path}`
                        : "https://zupimages.net/up/23/14/zdoe.jpg"
                    }
                    alt={actor.name}
                    onError={(e) => { e.target.src = "https://zupimages.net/up/23/14/zdoe.jpg"; }}
                  />
                </div>
                <div className="actor-info">
                  <h3 className="actor-name">{actor.name}</h3>
                  {actor.known_for_department && (
                    <p className="actor-department">
                      {getPersonDepartmentLabel(actor.known_for_department)}
                    </p>
                  )}
                  {actor.known_for && actor.known_for.length > 0 && (
                    <p className="actor-known-for">
                      {t('search.actor.knownFor')}: {actor.known_for.slice(0, 2).map(movie => movie.title || movie.name).join(", ")}
                    </p>
                  )}
                </div>
              </div>
            ))
          ) : searchType.key === "country" ? (
            // Affichage pour les films par pays
            data.map((m) => (
              <Movie
                movie={m}
                key={m.id}
                onClickMovie={() => history(`/ficheFilm/${m.id}`)}
                onError={replaceNoPoster}
                additionalClass="country-movie"
              />
            ))
          ) : (
            // Pour la recherche avancée
            searchType.key === "advanced" ? (
              data.map((m) => (
                <Movie
                  movie={m}
                  key={m.id}
                  onClickMovie={() => history(`/ficheFilm/${m.id}`)}
                  onError={replaceNoPoster}
                  additionalClass={
                    Number.isFinite(Number(m.movieFrenzyAverageRating))
                      ? "movie--moviefrenzy-rated"
                      : ""
                  }
                />
              ))
            ) : (
              <div className="placeholder-container">
                <h4>{t('search.results.featureComingSoon')}</h4>
              </div>
            )
          )}
        </ul>
      ) : !isLoading ? (
        <div className="placeholder-container">
          <h4 className="noResultSearch">{t('search.results.noResults')}</h4>
        </div>
      ) : null}

      {/* Pagination */}
      {!isLoading && shouldShowResults && data.length > 0 && (
        <div className="pagination">
          <button
            onClick={prevPage}
            disabled={currentPage === 1}
          >
            {t('search.pagination.previousPage')}
          </button>

          <span className="currentPage">
            {t('search.pagination.page')} {currentPage}
          </span>

          <button onClick={nextPage}>
            {t('search.pagination.nextPage')}
          </button>
        </div>
      )}
    </div>
  );

};

export default SearchMovie;
